import { type AuditBatch, type AuditTask, type EntityId } from '../api'

export interface QualityAuditListWorkflowsResponse {
  name: string // name is “service/vendor/workflow”
  auditableTaskCount?: number
  approvedTaskCount?: number
  rejectedTaskCount?: number
  firstPendingSlaDueDate?: number
  slaDueDate?: number
}

export interface QualityAuditWorkflowBatchListResponse {
  demeterServiceListAuditBatches: {
    auditBatches: AuditBatch[]
    totalResultCount: string
  }
}

export interface QualityAuditWorkflowBatchRequest {
  client: string
  batchUuid: string
  returnSampleTasks: boolean
  page: {
    offset: number
    limit: number
  }
}

export interface QualityAuditWorkflowBatchResponse {
  auditBatch: QualityAuditWorkflowBatch
}

export interface QualityAuditWorkflowBatch {
  batchId?: number
  batchUuid?: string
  isExplicitFailedRedrive?: boolean
  batchState?: string
  slaDueDate?: number
  workflow?: string
  version?: number
  sampleTasks?: QualityAuditWorkflowTask[]
  auditableTaskCount?: number
  recommendedSampleSize?: number
  sampleTaskCount?: number
  auditedTaskCount?: number
  createdAt?: number
  completedAt?: number
}

export interface QualityAuditWorkflowTaskComponent {
  type: string
  name: string
  value: string
  intComment: string
  extComment: string
  explicitAuditFailed: boolean
}

export interface QualityAuditWorkflowUpdateTaskComponentRequest {
  type: string
  name: string
  intComment: string
  extComment: string
  explicitAuditFailed: boolean
}

export interface QualityAuditWorkflowUpdateTaskRequest {
  client: string
  taskUuid: string
  state: string
  intComment: string
  extComment: string
  version: string
  taskComponents: QualityAuditWorkflowUpdateTaskComponentRequest[]
}

export interface QualityAuditWorkflowUpdateTaskResponse {
  message: string
  version: number
  batchVersion: number
}

export interface QualityAuditWorkflowTask {
  taskUuid: string
  taskState: string
  workflow: string
  intComment: string
  extComment: string
  inputDateString: string
  version: number
  taskComponents: QualityAuditWorkflowTaskComponent[]
  createdAt: number
  completedAt: number
}

export interface QualityAuditTaskComponentDatum {
  name: string
  type: string
  label: string
  value: string
  explicitAuditFailed: boolean
  groupName: string
  suggestedValue: string
  intComment: string
  extComment: string
  createdAt: number
  completedAt: number
  slaDueDate: number
}

export interface QualityAuditTaskDatum {
  taskUuid: string
  state: string
  entityId: EntityId
  intComment: string
  extComment: string
  version: string
  inputData: string
  components: {
    [type: string]: {
      [name: string]: QualityAuditTaskComponentDatum
    }
  }
  groupedComponents: {
    [name: string]: QualityAuditTaskComponentDatum[]
  }
  createdAt: number
  completedAt: number
  slaDueDate: number
  reviewState: string
}

export interface QualityAuditWorkflowListDatum {
  id: string
  name: string
  service: string
  vendor: string
  auditableTaskCount?: string
  approvedTaskCount?: string
  rejectedTaskCount?: string
  isSubscribed: boolean
  isFavourite: boolean
  slaDueDate?: number
  favWorkflowVersion: string
  favWorkflowNames: string[]
}

export interface QualityAuditWorkflowBatchListDatum {
  batchId?: string
  batchUuid?: string
  isExplicitFailedRedrive?: boolean
  batchState?: string
  slaDueDate?: number
  workflow?: string
  version?: string
  auditableTaskCount?: string
  auditedTaskCount?: string
  recommendedSampleSize?: string
  sampleTaskCount?: string
  createdAt?: number
  completedAt?: number
  workflowService?: string
  workflowVendor?: string
  workFlowName?: string
  description?: string
  secondaryStateId?: string
  metadata: { [key: string]: any }
  owner?: string
  assignee?: string
  reviewer?: string
  incorrectTaskCount?: string
}

export enum QualityAuditTaskStateFilter {
  PAST_DUE = 'past_due',
  PENDING = 'pending',
  REVIEWING = 'reviewing',
  COMPLETED = 'completed',
}

export enum QualityAuditWorkflowFilter {
  FAVOURITE_WORKFLOWS = 'favourite_workflows',
  OUTSOURCE_WORKFLOWS = 'outsource_workflows',
  PROBLEM_TRACKER_WORKFLOWS = 'problem_tracker_workflows',
  AUTOMATED_DETECT_WORKFLOWS = 'automated_detect_workflows',
  AUTOMATED_FIX_WORKFLOWS = 'automated_fix_workflows',
  ML_WORKFLOWS = 'ml_workflows',
  MEASURE_AND_DETECT = 'measure_and_detect',
  HUMAN_EVAL_AUTOMATED_FIX_INGESTION_WORKFLOWS = 'human_eval_automated_fix_ingestion_workflows',
  IMAGE_ATTRIBUTES_EXTRACTION_WORKFLOWS = 'image_attributes_extraction_workflows',
}

export enum QualityAuditTaskState {
  REJECTED = 'rejected',
  PENDING = 'pending',
  APPROVED = 'approved',
}

export enum QualityAuditReviewState {
  CORRECT = 'correct',
  INCORRECT = 'incorrect',
  OVERRIDE = 'override',
  PENDING = 'pending',
  ALL = '*',
}

export enum QualityAuditBatchState {
  PENDING = 'pending',
  REJECTED = 'rejected',
  APPROVED = 'approved',
  BY_PASSED = 'bypassed',
  COLLECTING_TASKS = 'collecting_tasks',
  REVIEWING = 'reviewing',
  ERRORED = 'errored',
  FORCE_REJECTED = 'force_rejected',
}

export enum QualityAuditBatchAction {
  COMPLETE = 'complete',
  FORCE_REJECTED = 'force_reject',
  RECALCULATE_PRECISION_RECALL = 'recalculate_precision_recall',
  BYPASS = 'bypass',
  SET_COMMENTS = 'set_comments',
  SET_METADATA = 'set_metadata',
  SET_ASSIGNEE = 'set_assignee',
  SET_REVIEWER = 'set_reviewer',
  SET_OWNER = 'set_owner',
  CHANGE_SAMPLE_SIZE = 'change_sample_size',
  CHANGE_DUE_DATE = 'change_due_date',
  CHANGE_SECONDARY_STATE = 'change_secondary_state',
  SET_AUDIT_FAILED_FLAG_TO_COMPONENTS = 'set_audit_failed_flag_to_components',
  SET_STATE_ON_PENDING_TASKS = 'set_state_on_pending_tasks',
}

export const QualityAuditDateFormat = 'MMMM D YYYY, h:mm:ss a z'

export enum QualityAuditSettings {
  favouriteWorkflows = 'quality_audit.workflow_favourites',
}

export const QualityAuditUserSettingConfigs = {
  appName: 'rpp',
  domain: 'catalog_admin',
}

export interface QualityAuditWorkflowFlagModalState {
  taskUuid: string
  extComment: string
  intComment: string
  suggestedValue?: string
  explicitAuditFailed?: boolean
  name?: string
  type?: string
}

export interface QualityAuditWorkflowBatchSecondaryState {
  id?: string | undefined
  name?: string | undefined
}

export const QualityAuditWorkflowBatchSecondaryStateDefaults = {
  NEW: '1',
  DONE: '9',
}

export interface AuditBatchResponse {
  auditBatchData: AuditBatch
  totalTasks: number
  batchId: string
  tasks: AuditTask[]
  workFlowId: string
  workFlowName: string
}

export interface QualityAuditWorkflowFeatureConfig {
  [name: string]: string
}

export interface QualityAuditWorkflowColumnConfig {
  alias: string
  type: AuditWorkflowColumnConfigType | string
  freezable?: boolean
  inputs: QualityAuditWorkflowColumnConfigInput[]
  features?: string
}

export interface QualityAuditWorkflowColumnConfigInput {
  type?: string
  name?: string
  group_name?: string
  flaggable?: boolean
  suggestible?: boolean
  searchable?: boolean
  subType?: AuditWorkflowColumnConfigType
  subAlias?: string
  features?: string
}

export interface QualityAuditWorkflowFlagGroupModalState {
  task: QualityAuditTaskDatum
  component?: QualityAuditTaskComponentDatum
  columnConfig: QualityAuditWorkflowColumnConfig
  components: QualityAuditTaskComponentDatum[]
  flaggable: boolean
  suggestible: boolean
  commentable: boolean
  isReadOnly?: boolean
  isSuggestibleDisabled?: boolean
}

export const QAS_CLIENT = 'clients/2'

export enum AuditWorkflowColumnConfigType {
  string = 'string',
  labeled_image = 'labeled_image',
  long_string = 'long_string',
  image = 'image',
  link = 'link',
  entity_id = 'entity_id',
  product_link = 'product_link',
  retail_product_link = 'retail_product_link',
  item_link = 'item_link',
  multi_type = 'multi_type',
  list_of_images = 'list_of_images',
  taxonomy_selector = 'taxonomy_selector',
  single_choice = 'single_choice',
  multi_choice = 'multi_choice',
  size = 'size',
  store_front_product_link = 'store_front_product_link',
  classification_error = 'classification_error',
  user = 'user',
  google_link = 'google_link',
  json = 'json',
}

export enum QualityAuditColumnConfigInputSelectionType {
  single = 'single',
  group = 'group',
}

export enum FeatureConfigKey {
  OutsourceVendor = 'outsource_vendor',
  OnlySamaManagerCompleteBatch = 'only_sama_manager_complete_batch',
  NoAuditWithoutAssigned = 'no_audit_without_assigned',
  ShowApproved = 'show_approved',
  DisableReject = 'disable_reject',
  ShowInternalCommentFilter = 'show_internal_comment_filter',
  ShowExternalCommentFilter = 'show_external_comment_filter',
  PrecisionAndRecall = 'precision_and_recall',
  MaxAccumulatingDays = 'max_accumulating_days',
  EnableAccumulatingTasks = 'enable_accumulating_tasks',
  MinTasksPerBatch = 'min_tasks_per_batch',
  MaxTasksPerBatch = 'max_tasks_per_batch',
  IsolateBySubmissionUuid = 'isolate_by_submission_uuid',
  EnableDeduplicatingEntityIds = 'enable_deduplicating_entity_ids',
  EnableDeduplicatingBulkEditTaxonomyRequests = 'enable_deduplicating_bulk_edit_taxonomy_requests',
  UseSequentialSamplingForReview = 'use_sequential_sampling_for_review',
}

export interface OptionValue {
  label: string
  value?: string
  values?: string[]
  index?: number
}

export interface ColumnFeatures {
  hideIfEmpty?: boolean
}

export interface SingleChoiceColumnFeatures extends ColumnFeatures {
  customType?: 'components'
  customComponents?: { type: string; name: string }[]
  hideComments?: boolean
  hideCommentsOnCustom?: boolean
}

// Reusable filter type for quality audit workflow components
export interface AuditTaskComponentValueFilter {
  type: string
  name: string
  groupName?: string
  values: string[]
}

// Type alias for array of component filters
export type AuditTaskComponentValueFilters = AuditTaskComponentValueFilter[]
